import React from 'react';
import { AlertOctagon, Wallet, Clock, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ComprovacaoContratoResumo } from '../hooks/useComprovacaoContratoMap';
import type { ComprovacaoFinanceiroPorContrato } from '../hooks/useComprovacaoFinanceiroResumo';

interface Props {
  comprovacaoMap: Record<string, ComprovacaoContratoResumo>;
  financeiroMap: Record<string, ComprovacaoFinanceiroPorContrato>;
}

const formatBRL = (n: number) =>
  n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

/**
 * Header executivo da IFMedicoesPage.
 * KPIs derivados exclusivamente de `comprovacaoMap` + `financeiroMap`.
 * Apresentação pura — não consulta backend, não duplica lógica.
 * Oculto quando todos os contratos estão saudáveis (nada exige atenção).
 */
export const IFExecutiveSummary: React.FC<Props> = ({ comprovacaoMap, financeiroMap }) => {
  const contratos = Object.values(comprovacaoMap);

  const criticos = contratos.filter(c => c.status_risco === 'critico').length;
  const piorAtraso = contratos.reduce(
    (acc, c) => Math.max(acc, c.pior_atraso_dias ?? 0),
    0,
  );
  const valorEmRisco = contratos
    .filter(c => c.status_risco !== 'saudavel')
    .reduce((acc, c) => acc + (financeiroMap[c.contrato_id]?.gap_comprovacao ?? 0), 0);

  // Oculta o bloco quando não há nada que exija atenção
  const allHealthy = contratos.length > 0 && criticos === 0 && piorAtraso === 0 && valorEmRisco <= 0;
  if (allHealthy || contratos.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <section
        aria-label="Visão de Risco da Carteira"
        className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-3"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground tracking-tight">
            Visão de Risco da Carteira
          </h3>
          <span className="text-xs text-muted-foreground">— atenção imediata</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard
            icon={<AlertOctagon className="h-4 w-4" />}
            label="Contratos críticos"
            value={String(criticos)}
            tone={criticos > 0 ? 'critical' : 'neutral'}
          />

          <KpiCard
            icon={<Wallet className="h-4 w-4" />}
            label="Valor aprovado sem comprovante de pagamento"
            value={formatBRL(Math.max(0, valorEmRisco))}
            tone={valorEmRisco > 0 ? 'warning' : 'neutral'}
            tooltip="Soma dos valores de medições já aprovadas para as quais ainda não há evidência de pagamento à contratada dentro do prazo de comprovação definido (SLA). Indica risco de governança, não perda financeira."
          />

          <KpiCard
            icon={<Clock className="h-4 w-4" />}
            label="Pior atraso de comprovante"
            value={`${piorAtraso} ${piorAtraso === 1 ? 'dia' : 'dias'}`}
            tone={piorAtraso > 30 ? 'critical' : piorAtraso > 0 ? 'warning' : 'neutral'}
          />
        </div>
      </section>
    </TooltipProvider>
  );
};

type Tone = 'critical' | 'warning' | 'neutral';

interface KpiProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: Tone;
  tooltip?: string;
}

const toneStyles: Record<Tone, { value: string; icon: string; ring: string }> = {
  critical: {
    value: 'text-destructive',
    icon: 'text-destructive',
    ring: 'border-destructive/30 bg-destructive/[0.04]',
  },
  warning: {
    value: 'text-amber-600 dark:text-amber-500',
    icon: 'text-amber-600 dark:text-amber-500',
    ring: 'border-amber-500/30 bg-amber-500/[0.04]',
  },
  neutral: {
    value: 'text-foreground',
    icon: 'text-muted-foreground',
    ring: 'border-border/60 bg-muted/20',
  },
};

const KpiCard: React.FC<KpiProps> = ({ icon, label, value, tone, tooltip }) => {
  const styles = toneStyles[tone];
  return (
    <div className={cn('rounded-md border px-4 py-3 transition-colors', styles.ring)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn('shrink-0', styles.icon)}>{icon}</span>
          <p className="text-xs font-medium text-muted-foreground truncate">{label}</p>
        </div>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Mais informações"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <p className={cn('mt-1.5 text-2xl font-semibold tracking-tight tabular-nums', styles.value)}>
        {value}
      </p>
    </div>
  );
};

export default IFExecutiveSummary;
